import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api.js";
import { levelName } from "../lib/levels.js";
import { makeT, QUESTS_FR, SECTIONS_FR, qFr } from "../lib/i18n.js";
import QuestionCard from "../components/QuestionCard.jsx";
import ReactSection from "../components/ReactSection.jsx";
import ReadSection from "../components/ReadSection.jsx";

export default function ParticipantApp() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Optional deep-link: /j/<token>?q=q7 opens straight into that quest.
  const [view, setView] = useState(() => new URLSearchParams(location.search).get("q") || "map");
  const [consenting, setConsenting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [lang, setLang] = useState(() => {
    // ?lang=fr in the link wins once (and sticks); else the saved choice; else English.
    const q = new URLSearchParams(location.search).get("lang");
    if (q === "fr" || q === "en") { try { localStorage.setItem("intake_lang", q); } catch {} return q; }
    return localStorage.getItem("intake_lang") || "en";
  });
  const t = makeT(lang);
  const setLangPersist = (l) => { setLang(l); try { localStorage.setItem("intake_lang", l); } catch {} };
  // Quest name/purpose/intro in the active language.
  const qMeta = (q) => lang === "fr" && QUESTS_FR[q.quest_key]
    ? { name: QUESTS_FR[q.quest_key].name, purpose: QUESTS_FR[q.quest_key].purpose, intro: QUESTS_FR[q.quest_key].intro }
    : { name: q.name, purpose: q.purpose, intro: QUEST_INTRO[q.quest_key] };

  // Failed saves are kept per-question and retried (timer + on reconnect),
  // so a dropped phone connection never loses an answer.
  const pending = useRef(new Map());
  const retryTimer = useRef(null);

  async function load() {
    try { setData(await api.session(token)); }
    catch (e) { setError(e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  useEffect(() => {
    const flush = () => flushPending();
    window.addEventListener("online", flush);
    return () => window.removeEventListener("online", flush);
  }, []);

  // Browser tab title: "LinkedIn intake · <name>".
  useEffect(() => {
    const name = data?.participant?.full_name;
    if (name) document.title = `LinkedIn intake · ${name}`;
  }, [data]);

  // Jump to the top when opening a different part.
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, [view]);

  // First run after consent: if there's a data read to show, land there before the
  // dashboard. Once seen (or if deep-linked to a quest), go straight to the dashboard.
  const didInitView = useRef(false);
  useEffect(() => {
    if (!data || !data.participant?.consent_at || didInitView.current) return;
    didInitView.current = true;
    const qParam = new URLSearchParams(location.search).get("q");
    if (qParam) return;
    const seen = (() => { try { return localStorage.getItem("intake_seen_data_" + data.participant.id); } catch { return null; } })();
    if (data.audit && !seen) setView("data");
  }, [data]);
  const markDataSeen = () => { try { localStorage.setItem("intake_seen_data_" + data.participant.id, "1"); } catch {} };

  function scheduleRetry() {
    clearTimeout(retryTimer.current);
    retryTimer.current = setTimeout(flushPending, 4000);
  }
  async function flushPending() {
    if (!pending.current.size) return;
    for (const [key, payload] of [...pending.current]) {
      const res = await sendSave(payload);
      if (res) pending.current.delete(key);
    }
    if (pending.current.size) { setSaveState("error"); scheduleRetry(); }
    else setSaveState("saved");
  }

  async function sendSave(payload) {
    try {
      const res = await api.save({ token, ...payload });
      applyAnswer(payload, res);
      return res;
    } catch { return null; }
  }

  // Optimistic autosave used by every question. Updates local state immediately,
  // persists in the background, and queues a retry on failure.
  async function persist(payload) {
    setSaveState("saving");
    applyAnswer(payload); // optimistic, dots/tags update without waiting on the network
    const res = await sendSave(payload);
    if (res) { pending.current.delete(payload.question_key); setSaveState("saved"); return res; }
    pending.current.set(payload.question_key, payload);
    setSaveState("error"); scheduleRetry();
    return null;
  }

  // Reflect one answer in local state (optimistic pre-network, and again with server stats).
  function applyAnswer(payload, res) {
    setData((d) => {
      if (!d) return d;
      const participant = { ...d.participant };
      if (res && res.pct !== undefined) {
        participant.overall_progress_pct = res.pct;
        participant.xp = res.xp; participant.level = res.level;
      }
      let answers = d.answers.filter((a) => a.question_key !== payload.question_key);
      if (payload.action !== "clear") {
        answers = [...answers, {
          question_key: payload.question_key, quest_key: payload.quest_key,
          answer_type: payload.answer_type || "text",
          value_text: payload.action === "skip" ? null : (payload.value_text ?? null),
          value_json: payload.value_json ? JSON.stringify(payload.value_json) : null,
          skipped: payload.action === "skip" ? 1 : 0,
        }];
      }
      return { ...d, participant, answers };
    });
  }

  if (error) return <Centered title={t("badLink")}>{error}</Centered>;
  if (!data) return (
    <div className="app-shell qf-section">
      <div className="qf-wrap loading"><span className="spinner" aria-hidden /><span className="muted-sm">{t("loading")}</span></div>
    </div>
  );

  const { participant, questionnaire } = data;
  const answersByKey = Object.fromEntries((data.answers || []).map((a) => [a.question_key, a]));
  const peerLink = `${location.origin}/m/${participant.peer_link_token || ""}`;

  if (!participant.consent_at) {
    return <Welcome name={participant.full_name} busy={consenting} t={t} lang={lang} onLang={setLangPersist} onStart={async () => {
      setConsenting(true);
      try { await api.consent(token); await load(); } finally { setConsenting(false); }
    }} />;
  }

  if (submitted) return <Completion t={t} />;

  // The data read is a full-width standalone page, shown before the dashboard.
  if (view === "data" && data.audit) {
    return (
      <div className="app-shell">
        <TopBar participant={participant} saveState={saveState} t={t} lang={lang} onLang={setLangPersist}
          onHome={() => { markDataSeen(); setView("map"); }} onDossier={() => setView("dossier")} hideMeta />
        <div className="qf-wrap qf-section">
          <DataReveal audit={data.audit} participant={participant} t={t} lang={lang}
            onContinue={() => { markDataSeen(); setView("map"); }} />
        </div>
      </div>
    );
  }

  // Sections A and B only exist once the operator has published something into them.
  const readBlocks = data.read_blocks || [];
  const reactItems = data.react_items || [];

  return (
    <div className="app-shell">
      <TopBar participant={participant} saveState={saveState} t={t} lang={lang} onLang={setLangPersist}
        onHome={() => setView("map")} onDossier={() => setView("dossier")}
        onData={data.audit ? () => setView("data") : null} />
      <div className="qf-wrap qf-section shell">
        <Sidebar questionnaire={questionnaire} answersByKey={answersByKey} participant={participant}
          view={view} onOpen={setView} qMeta={qMeta} t={t}
          hasRead={readBlocks.length > 0} hasReact={reactItems.length > 0} />
        <div className="content">
          {view === "map" && (
            <QuestMap questionnaire={questionnaire} answersByKey={answersByKey}
              participant={participant} data={data} t={t} lang={lang} qMeta={qMeta}
              onOpen={setView} onSubmitted={() => setSubmitted(true)} token={token} />
          )}
          {view === "read" && (
            <ReadSection blocks={readBlocks} participant={participant} onBack={() => setView("map")} />
          )}
          {view === "react" && (
            <ReactSection items={reactItems} responses={data.react_responses}
              token={token} reload={load} onBack={() => setView("map")} />
          )}
          {view === "dossier" && (
            <DossierPreview questionnaire={questionnaire} answersByKey={answersByKey}
              assets={data.assets} onBack={() => setView("map")} token={token} t={t} lang={lang} qMeta={qMeta} />
          )}
          {questionnaire.some((q) => q.quest_key === view) && (() => {
            const order = [...SECTIONS.flatMap((s) => s.quests), "wrap"].filter((k) => questionnaire.some((q) => q.quest_key === k));
            const idx = order.indexOf(view);
            const nextKey = idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
            const nextQ = nextKey ? questionnaire.find((q) => q.quest_key === nextKey) : null;
            return (
              <QuestView quest={questionnaire.find((q) => q.quest_key === view)}
                data={data} answersByKey={answersByKey} peerLink={peerLink} t={t} lang={lang} qMeta={qMeta}
                token={token} persist={persist} reload={load} onBack={() => setView("map")}
                onNext={nextKey ? () => setView(nextKey) : null} nextName={nextQ ? qMeta(nextQ).name : null}
                position={{ idx: idx + 1, total: order.length }} />
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// Quest-level status from its questions.
function questStatus(quest, answersByKey) {
  const states = quest.questions.map((x) => {
    const a = answersByKey[x.question_key];
    return a ? (a.skipped ? "skipped" : "answered") : "untouched";
  });
  const resolved = states.filter((s) => s !== "untouched").length;
  const total = quest.questions.length;
  const k = resolved === 0 ? "untouched" : resolved >= total ? "done" : "partial";
  return { k, resolved, total };
}

// Circular progress ring for the dashboard.
function Ring({ pct }) {
  const r = 27, c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return (
    <svg className="ring" viewBox="0 0 64 64" width="72" height="72" role="img" aria-label={`${pct}% complete`}>
      <circle className="ring__track" cx="32" cy="32" r={r} />
      <circle className="ring__fill" cx="32" cy="32" r={r} strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 32 32)" />
      <text className="ring__t" x="32" y="33" textAnchor="middle" dominantBaseline="central">{pct}%</text>
    </svg>
  );
}

// Status dot that becomes an animated green check when a section is complete.
const CheckIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
);
function Dot({ k, base }) {
  const cls = k === "done" ? `${base} ${base}--done check-pop` : k === "partial" ? `${base} ${base}--partial` : base;
  return <span className={cls} aria-hidden>{k === "done" ? <CheckIcon /> : null}</span>;
}

const Chevron = () => (
  <svg className="part__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>
);

// ---- Sidebar (desktop nav) --------------------------------------------------
function Sidebar({ questionnaire, answersByKey, participant, view, onOpen, qMeta, t, hasRead, hasReact }) {
  const byKey = Object.fromEntries(questionnaire.map((q) => [q.quest_key, q]));
  const groups = [...SECTIONS];
  const secTitle = (title) => (isFr(t) && SECTIONS_FR[title]) ? SECTIONS_FR[title] : title;
  return (
    <aside className="sidebar">
      <div className="side-prog">
        <div className="side-prog__row"><span>{t("mProgress")}</span><b>{participant.overall_progress_pct}%</b></div>
        <div className="qf-bar"><span className="qf-bar__fill" style={{ "--qf-fill": `${participant.overall_progress_pct}%` }} /></div>
      </div>
      <nav className="nav">
        {/* Optional surfaces. No dot, because there is no state to be behind on. */}
        {(hasRead || hasReact) && (
          <div className="nav__group">
            <div className="nav__label">Before Tuesday</div>
            {hasRead && (
              <button className={`nav__item nav__item--plain ${view === "read" ? "nav__item--on" : ""}`} onClick={() => onOpen("read")}>
                What I've read
              </button>
            )}
            {hasReact && (
              <button className={`nav__item nav__item--plain ${view === "react" ? "nav__item--on" : ""}`} onClick={() => onOpen("react")}>
                React if you want
              </button>
            )}
          </div>
        )}
        {groups.map((sec) => (
          <div className="nav__group" key={sec.title}>
            <div className="nav__label">{secTitle(sec.title)}</div>
            {sec.quests.map((k) => {
              const q = byKey[k]; if (!q) return null;
              const st = questStatus(q, answersByKey);
              return (
                <button key={k} className={`nav__item ${view === k ? "nav__item--on" : ""}`} onClick={() => onOpen(k)}>
                  <Dot k={st.k} base="nav__dot" />
                  <span style={{ flex: 1, minWidth: 0 }}>{qMeta(q).name}</span>
                </button>
              );
            })}
          </div>
        ))}
        {byKey.wrap && (
          <div className="nav__group">
            <button className={`nav__item ${view === "wrap" ? "nav__item--on" : ""}`} onClick={() => onOpen("wrap")}>
              <span className="nav__dot" /><span>{qMeta(byKey.wrap).name}</span>
            </button>
          </div>
        )}
      </nav>
    </aside>
  );
}

// Cheap FR check: the "done" word differs between locales.
const isFr = (t) => t("doneWord") === "fait";

// ---- Language toggle --------------------------------------------------------
function LangToggle({ lang, onLang }) {
  return (
    <div className="seg" role="group" aria-label="Language">
      {[["en", "EN"], ["fr", "FR"]].map(([code, short]) => (
        <button key={code} className={lang === code ? "on" : ""} aria-pressed={lang === code}
          aria-label={code === "en" ? "English" : "Français"} onClick={() => onLang(code)}>{short}</button>
      ))}
    </div>
  );
}

// ---- Top bar ----------------------------------------------------------------
const SAVE_LABEL = { saving: "Saving…", saved: "Saved", error: "Offline, will retry" };
function TopBar({ participant, saveState, onHome, onDossier, onData, t, lang, onLang, hideMeta }) {
  const p = participant;
  return (
    <div className="topbar">
      <div className="qf-wrap topbar__inner">
        <button className="topbar__home" onClick={onHome} aria-label="Dashboard" title="Dashboard">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 10.5 12 4l9 6.5" /><path d="M5 9.5V20h14V9.5" />
          </svg>
        </button>
        <div className="topbar__grow" />
        <div className="topbar__meta">
          <LangToggle lang={lang} onLang={onLang} />
          {!hideMeta && <>
            {onData && <button className="link-btn topbar__data" onClick={onData}>{t("revisit")}</button>}
            <span><span className="qf-num">{p.overall_progress_pct}%</span> {t("doneWord")}</span>
            {saveState !== "idle" && (
              <span className={`saved ${saveState === "error" ? "saved--err" : ""}`}>
                {saveState === "saved" && <b>✓</b>}{SAVE_LABEL[saveState]}
              </span>
            )}
            <button className="link-btn" onClick={onDossier}>{t("review")}</button>
          </>}
        </div>
      </div>
    </div>
  );
}

// ---- Welcome + consent ------------------------------------------------------
function Welcome({ name, onStart, busy, t, lang, onLang }) {
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState(false);
  const start = () => { if (!ok) { setErr(true); return; } onStart(); };
  const first = name.split(" ")[0];
  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="qf-wrap topbar__inner">
          <span className="brandmark">{t("welcomeBrand")}</span>
          <div className="topbar__grow" />
          <LangToggle lang={lang} onLang={onLang} />
        </div>
      </div>
      <div className="qf-wrap qf-section">
        <div className="overview">
          <div className="cover"><img src="/img/welcome.jpg" alt="" /></div>
          <div className="intro-head">
            <h1 className="dash__title">{t("welcomeTitle", first)}</h1>
            <p className="qf-lead">{t("welcomeLead")}</p>
          </div>
          <div className="qf-card intro-card">
            <label className={`choice ${err ? "choice--err" : ""}`} style={{ cursor: "pointer" }}>
              <input type="checkbox" checked={ok} onChange={(e) => { setOk(e.target.checked); if (e.target.checked) setErr(false); }} />
              <span>{t("consent")}</span>
            </label>
            <div className="intro-actions">
              <button className="qf-btn qf-btn--primary" disabled={busy} onClick={start}>
                {busy ? t("oneMoment") : t("start")}
              </button>
              {err && <p className="field-err">{t("consentErr")}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Home: sections grouping the nine parts --------------------------------
// Time-sensitive parts come first: the mirror (others need time to reply) and the
// personality test (an external step) both live in the opening section.
const SECTIONS = [
  { title: "Your voice", cover: "/img/band1.jpg", quests: ["q3", "q1", "q2"] },
  { title: "Your goals and audience", cover: "/img/band2.jpg", quests: ["q4", "q5", "q6"] },
  { title: "Your ideas and how you'll work", cover: "/img/band3.jpg", quests: ["q7", "q8", "q9"] },
];

const coverFor = (qk) => (SECTIONS.find((s) => s.quests.includes(qk)) || {}).cover || "/img/home.jpg";
const ordinal = (n) => n + (["th", "st", "nd", "rd"][(n % 100 - 20) % 10] || ["th", "st", "nd", "rd"][n] || "th");

// Warm, intent-framed intros shown under each quest title.
const QUEST_INTRO = {
  q1: "In this section, we get a feel for your natural way of communicating.",
  q2: "Here we look at your usual communication style, in your own words.",
  q3: "A quick step: send a link so a few people who know you can describe you.",
  q4: "What you actually want LinkedIn to do for you.",
  q5: "The exact people you want to reach, and what they should do next.",
  q6: "Your lane: your references and the topics you can own.",
  q7: "The good stuff. Your decisions, opinions, and convictions.",
  q8: "What only you can bring: proof, access, and formats.",
  q9: "How much you can realistically sustain, and your red lines.",
  wrap: "Anything we didn't ask that we should know.",
};

// Tags on quest cards. Never blue (blue stays for links).
const TAGS = {
  q4: { cls: "tag--core", label: "core" },        // what everything else builds on
};

function tagsFor(q) {
  const t = [];
  if (TAGS[q.quest_key]) t.push(TAGS[q.quest_key]);
  if (q.deep) t.push({ cls: "tag--deep", label: "deep" });
  return t;
}

function QuestMap({ questionnaire, answersByKey, participant, data, onOpen, onSubmitted, token, t, lang, qMeta }) {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try { await api.submit(token); onSubmitted(); } finally { setSubmitting(false); }
  };

  const byKey = Object.fromEntries(questionnaire.map((q) => [q.quest_key, q]));
  const wrap = byKey.wrap;

  // Metrics.
  const total = questionnaire.flatMap((q) => q.questions).length;
  const answers = Object.values(answersByKey);
  const answered = answers.filter((a) => !a.skipped).length;
  const peers = data.peers?.length || 0;
  const pct = participant.overall_progress_pct;
  const resolvedCount = answers.length;
  // Next unfinished part, in section order. Only once they've actually started.
  const order = SECTIONS.flatMap((s) => s.quests);
  const nextKey = order.find((k) => byKey[k] && byKey[k].questions.some((q) => !answersByKey[q.question_key]));
  const nextQuest = resolvedCount > 0 && nextKey ? byKey[nextKey] : null;

  const answerLabel = (n) => isFr(t)
    ? `${n}${n === 1 ? "re" : "e"} réponse`
    : `${ordinal(n)} answer`;
  const PartRow = (q, wrapRow = false) => {
    const st = questStatus(q, answersByKey);
    const tags = tagsFor(q);
    const meta = qMeta(q);
    const wrapQ = q.questions[0];
    const wrapPrompt = wrapQ ? (isFr(t) && qFr(wrapQ.question_key).prompt ? qFr(wrapQ.question_key).prompt : wrapQ.prompt) : "";
    let dotK = st.k;
    let right = wrapRow ? null : `${st.resolved}/${st.total}`;
    // The mirror shows its own progression: clicked, then replies received.
    if (q.quest_key === "q3") {
      const copied = !!answersByKey["q3_mirror"] && !answersByKey["q3_mirror"].skipped;
      if (peers > 0) { right = answerLabel(peers); dotK = "done"; }
      else if (copied) { right = t("clicked"); dotK = "partial"; }
      else { right = ""; dotK = "untouched"; }
    }
    return (
      <button key={q.quest_key} className={`part ${wrapRow ? "part--wrap" : ""}`} onClick={() => onOpen(q.quest_key)}>
        <Dot k={dotK} base="part__dot" />
        <span className="part__main">
          <span className="part__name">{meta.name}{tags.map((tg) => <span key={tg.label} className={`tag ${tg.cls}`}>{isFr(t) ? t(tg.label) : tg.label}</span>)}</span>
          <span className="part__purpose">{wrapRow ? wrapPrompt : meta.purpose}</span>
        </span>
        <span className="part__right">{right && <span className="part__count">{right}</span>}<Chevron /></span>
      </button>
    );
  };

  return (
    <div className="overview">
      <div className="cover"><img src="/img/home.jpg" alt="" /></div>

      <div className="dash__head">
        <div>
          <div className="dash__eyebrow">{t("welcomeBack", participant.full_name.split(" ")[0])}</div>
          <h1 className="dash__title">{t("dashTitle")}</h1>
          <p className="qf-lead dash__orient">{t("orient")}</p>
        </div>
        <div className="dash__ring">
          <Ring pct={pct} />
          {pct >= 50 && <span className="dash__cheer">{pct >= 75 ? t("almostDone") : t("halfway")}</span>}
        </div>
      </div>

      <ThisWeek data={data} onOpen={onOpen} />

      <div className="metrics metrics--two">
        <div className="metric">
          <div className="metric__l">{t("mAnswered")}</div>
          <div className="metric__n">{answered}<small> / {total}</small></div>
        </div>
        <button className="metric metric--link" onClick={() => onOpen("q3")}>
          <div className="metric__l">{t("mReplied")}</div>
          <div className="metric__n">{peers}</div>
          <div className="metric__go">{t("openMirror")}</div>
        </button>
      </div>

      {nextQuest && (
        <div className="qf-card continue">
          <div><div className="continue__k">{t("pickUp")}</div><div className="continue__t">{qMeta(nextQuest).name}</div></div>
          <button className="qf-btn qf-btn--primary" onClick={() => onOpen(nextKey)}>{t("continue")}</button>
        </div>
      )}

      <div className="sections-mobile">
        <div className="qf-stack--lg">
          <div className="legend">
            <span><i className="done" /> {t("legendDone")}</span>
            <span><i className="partial" /> {t("legendStarted")}</span>
            <span><i /> {t("legendTodo")}</span>
          </div>
          {SECTIONS.map((sec) => (
            <section className="section" key={sec.title}>
              <div className="section__label">{isFr(t) && SECTIONS_FR[sec.title] ? SECTIONS_FR[sec.title] : sec.title}</div>
              <div className="partlist">{sec.quests.map((k) => byKey[k] && PartRow(byKey[k]))}</div>
            </section>
          ))}
          {wrap && <div className="partlist">{PartRow(wrap, true)}</div>}
        </div>
      </div>

      <div className="finalcard">
        {!confirming ? (
          <>
            <div className="finalcard__head">
              <span className="finalcard__icon" aria-hidden>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4 20-7z" /></svg>
              </span>
              <span className="finalcard__t">{t("sendingFinal")}</span>
            </div>
            <p className="muted-sm">{t("sendBody")}</p>
            <div><button className="qf-btn qf-btn--ghost" onClick={() => setConfirming(true)}>{t("imDone")}</button></div>
          </>
        ) : (
          <>
            <p><strong>{t("confirmSend")}</strong> {t("confirmSub")}</p>
            <div className="hstack">
              <button className="qf-btn qf-btn--primary" onClick={submit} disabled={submitting}>{submitting ? t("sending") : t("yesSend")}</button>
              <button className="subtle" onClick={() => setConfirming(false)}>{t("notYet")}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Optional surfaces on the dashboard -------------------------------------
// Sections A and B, offered rather than assigned. No dot, no count, no percentage:
// they sit outside the questionnaire and outside its denominator on purpose. The
// block only exists once the operator has published something into either one.
function ThisWeek({ data, onOpen }) {
  const blocks = data.read_blocks || [];
  const items = data.react_items || [];
  if (!blocks.length && !items.length) return null;
  const reacted = (data.react_responses || [])
    .filter((r) => r.verdict || r.note_text || r.audio_key || r.skipped).length;

  return (
    <div className="thisweek">
      <div className="section__label">Before Tuesday</div>
      <p className="thisweek__lead">
        Nothing here is required before Tuesday. If you have ten minutes and feel like
        reacting out loud, the voice button is there. If not, we'll go through it
        together on the 4th.
      </p>
      <div className="thisweek__cards">
        {blocks.length > 0 && (
          <button className="offer" onClick={() => onOpen("read")}>
            <span className="offer__t">What I've read</span>
            <span className="offer__s">Your decks, your LP report, your article, and thirty other funds. Nothing to fill in.</span>
            <span className="offer__go">Have a look <Chevron /></span>
          </button>
        )}
        {items.length > 0 && (
          <button className="offer" onClick={() => onOpen("react")}>
            <span className="offer__t">React if you want</span>
            <span className="offer__s">{items.length} things I'd like your gut reaction on. Voice is easiest.</span>
            <span className="offer__go">
              {reacted > 0 ? `${reacted} of ${items.length} reacted` : "Ten minutes, tops"} <Chevron />
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

// ---- "What your data reveals": standalone page shown before the dashboard -----
function Tile({ label, value, unit, sub }) {
  return (
    <div className="reveal-tile">
      <div className="reveal-tile__l">{label}</div>
      <div className="reveal-tile__n">{value}{unit && <small> {unit}</small>}</div>
      {sub && <div className="reveal-tile__s">{sub}</div>}
    </div>
  );
}

// Full month sequence between two ISO dates, so gaps (months with no posts) show honestly.
function monthSeq(first, last) {
  const out = [];
  let d = new Date(`${first.slice(0, 7)}-01T00:00:00Z`);
  const end = new Date(`${last.slice(0, 7)}-01T00:00:00Z`);
  let guard = 0;
  while (d <= end && guard++ < 60) { out.push(d.toISOString().slice(0, 7)); d.setUTCMonth(d.getUTCMonth() + 1); }
  return out;
}
const fmtMonth = (ym, months) => `${months[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;

// Monthly posting cadence. Bar HEIGHT carries engagement (how much each month
// landed); the number on top is the post COUNT. Gaps (empty months) show as dots.
function CadenceChart({ monthly, first, last, t }) {
  const months = t("monthShort");
  const byYm = Object.fromEntries(monthly.map((m) => [m.ym, m]));
  const data = monthSeq(first, last).map((ym) => {
    const m = byYm[ym];
    return { ym, count: m?.count || 0, eng: m?.eng ?? (m?.count || 0) };
  });
  const useEng = data.some((d) => d.eng > 0 && d.eng !== d.count);
  const metric = (d) => (useEng ? d.eng : d.count);
  const max = Math.max(1, ...data.map(metric));
  const W = 640, H = 170, padB = 26, padT = 18, padX = 10;
  const n = data.length, slot = (W - padX * 2) / n, barW = Math.min(30, slot * 0.55);
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Monthly posting cadence">
      <line x1="0" y1={H - padB} x2={W} y2={H - padB} className="chart__axis" />
      {data.map((d, i) => {
        const cx = padX + i * slot + slot / 2;
        const h = d.count === 0 ? 0 : Math.max(4, (metric(d) / max) * (H - padB - padT));
        return (
          <g key={d.ym}>
            {d.count > 0
              ? <><rect x={cx - barW / 2} y={H - padB - h} width={barW} height={h} rx="3" className="chart__bar">
                    <title>{d.count} post{d.count > 1 ? "s" : ""}{useEng ? ` · ${d.eng} interactions` : ""}</title>
                  </rect>
                  <text x={cx} y={H - padB - h - 5} className="chart__val" textAnchor="middle">{d.count}</text></>
              : <circle cx={cx} cy={H - padB} r="1.6" className="chart__zero" />}
          </g>
        );
      })}
      <text x={padX + slot / 2} y={H - 8} className="chart__lbl" textAnchor="middle">{fmtMonth(data[0].ym, months)}</text>
      {n > 1 && <text x={padX + (n - 1) * slot + slot / 2} y={H - 8} className="chart__lbl" textAnchor="middle">{fmtMonth(data[n - 1].ym, months)}</text>}
    </svg>
  );
}

// Format mix — horizontal magnitude bars, direct counts.
function FormatBars({ fmts }) {
  const max = Math.max(1, ...fmts.map(([, v]) => v));
  return (
    <div className="fbars">
      {fmts.map(([k, v]) => (
        <div key={k} className="fbar">
          <span className="fbar__k">{k}</span>
          <span className="fbar__track"><span className="fbar__fill" style={{ width: `${(v / max) * 100}%` }} /></span>
          <span className="fbar__v">{v}</span>
        </div>
      ))}
    </div>
  );
}

function DataReveal({ audit, participant, t, lang, onContinue }) {
  const a = audit;
  const first = participant.full_name.split(" ")[0];
  const fmts = Object.entries(a.formats || {}).sort((x, y) => y[1] - x[1]);
  return (
    <div className="reveal">
      <div className="cover reveal__cover"><img src="/img/band2.jpg" alt="" /></div>
      <div className="reveal__head">
        <div className="dash__eyebrow">{t("revealEyebrow")}</div>
        <h1 className="dash__title">{t("revealTitle", first)}</h1>
        <p className="qf-lead">{t("revealLead")}</p>
      </div>

      <div className="reveal__tiles">
        {a.per_month != null && <Tile label={t("dataCadence")} value={a.per_month} unit={t("perMonthShort")} />}
        <Tile label={t("avgEngLabel")} value={a.avg_eng} sub={`${a.avg_likes} · ${a.avg_comments} · ${a.avg_reposts}`} />
        <Tile label={t("totalPosts")} value={a.own_count} sub={a.repost_count ? `+ ${a.repost_count} ${t("dataReposts")}` : ""} />
        {(a.best_format || a.top_format) && <Tile label={t("bestFormat")} value={a.best_format || a.top_format} />}
      </div>

      <div className="reveal__grid">
        {a.monthly?.length > 0 && a.first_date && (
          <div className="qf-card reveal__chart">
            <div className="datastat__l">{t("revealRhythm")}</div>
            <CadenceChart monthly={a.monthly} first={a.first_date} last={a.last_date} t={t} />
            <div className="datastat__sub" style={{ marginTop: "var(--qf-2)" }}>{t("rhythmNote")}</div>
          </div>
        )}
        {fmts.length > 0 && (
          <div className="qf-card reveal__chart">
            <div className="datastat__l">{t("revealFormats")}</div>
            <FormatBars fmts={fmts} />
          </div>
        )}
      </div>

      {a.top_terms?.length > 0 && (
        <div className="qf-card">
          <div className="datastat__l">{t("dataTerms")}</div>
          <div className="datatags" style={{ marginTop: "var(--qf-3)" }}>
            {a.top_terms.map((x) => <span key={x.term} className="datatag">{x.term}</span>)}
          </div>
        </div>
      )}

      {a.ai?.themes?.length > 0 && (
        <div className="qf-card">
          <div className="datastat__l">{t("dataThemes")}</div>
          <ul className="datathemes">{a.ai.themes.map((th, i) => <li key={i}>{th.label}</li>)}</ul>
        </div>
      )}

      {a.top_post && (
        <div className="qf-card reveal__top">
          <div className="datastat__l">{t("dataTopPost")}</div>
          <p className="reveal__quote">“{a.top_post.text}…”</p>
          <div className="datastat__sub">{a.top_post.eng} {t("dataInteractions")} · {a.top_post.type}</div>
        </div>
      )}

      <div className="reveal__cta">
        <button className="qf-btn qf-btn--primary" onClick={onContinue}>{t("revealContinue")} →</button>
        <span className="muted-sm">{t("revealComeBack")}</span>
      </div>
    </div>
  );
}

// ---- One quest --------------------------------------------------------------
function QuestView({ quest, data, answersByKey, peerLink, token, persist, reload, onBack, onNext, nextName, position, t, lang, qMeta }) {
  const [showDepth, setShowDepth] = useState(false);
  const meta = qMeta(quest);
  const advance = onNext || onBack; // last section falls back to the dashboard

  const checkDepth = () => {
    // End-of-section: if answers here were short or several skipped, nudge once.
    let shortOrSkipped = 0;
    for (const q of quest.questions) {
      const a = answersByKey[q.question_key];
      if (!a || a.skipped) { shortOrSkipped++; continue; }
      if (a.value_text && a.value_text.trim().length < 40) shortOrSkipped++;
    }
    if (shortOrSkipped >= Math.ceil(quest.questions.length / 2)) setShowDepth(true);
    else advance();
  };

  return (
    <div className="quest">
      <div className="cover" style={{ marginBottom: "var(--qf-4)" }}><img src={coverFor(quest.quest_key)} alt="" /></div>
      <div>
        <div className="quest__nav">
          <button className="link-btn" onClick={onBack}>← {t("backToDash")}</button>
          {position && <span className="quest__pos">{t("sectionPos", position.idx, position.total)}</span>}
        </div>
        <h1 className="qf-title" style={{ marginTop: "var(--qf-3)" }}>{meta.name}</h1>
        {meta.intro && <p className="qf-lead" style={{ marginTop: "var(--qf-3)" }}>{meta.intro}</p>}
        {tagsFor(quest).length > 0 && (
          <span className="tags-row" style={{ marginTop: "var(--qf-3)" }}>
            {tagsFor(quest).map((tg) => <span key={tg.label} className={`tag ${tg.cls}`}>{isFr(t) ? t(tg.label) : tg.label}</span>)}
          </span>
        )}
      </div>

      <div className="qlist">
        {quest.questions.map((q) => (
          <QuestionCard key={q.question_key} q={q} answer={answersByKey[q.question_key]}
            assets={data.assets} postPool={data.post_pool} peers={data.peers} peerLink={peerLink}
            leaderName={data.participant?.full_name} token={token} persist={persist} reload={reload}
            t={t} lang={lang} audit={data.audit} />
        ))}
      </div>

      {showDepth ? (
        <div className="qf-note">
          <p>{t("depth")}</p>
          <div className="actions">
            <button className="qf-btn qf-btn--ghost" onClick={() => setShowDepth(false)}>{t("addMore")}</button>
            <button className="qf-btn qf-btn--primary" onClick={advance}>{t("moveOn")}</button>
          </div>
        </div>
      ) : (
        <div className="quest__foot">
          <button className="qf-btn qf-btn--primary" onClick={checkDepth}>
            {onNext ? `${t("nextUp")}: ${nextName} →` : t("done")}
          </button>
          {onNext && <button className="subtle" onClick={onBack}>{t("backToDash")}</button>}
        </div>
      )}
    </div>
  );
}

// ---- Dossier preview (read-only, no scores) ---------------------------------
function DossierPreview({ questionnaire, answersByKey, assets, onBack, t, lang, qMeta }) {
  const fr = isFr(t);
  return (
    <div className="qf-stack--lg">
      <button className="link-btn" onClick={onBack}>← {t("back")}</button>
      <div><h1 className="qf-title">{t("yourAnswers")}</h1>
        <p className="qf-lead">{t("thin")}</p></div>
      {questionnaire.map((quest) => (
        <div key={quest.quest_key} className="qf-card">
          <div className="qf-label qf-label--accent">{qMeta(quest).name}</div>
          <div className="qf-stack" style={{ marginTop: "var(--qf-3)" }}>
            {quest.questions.map((q) => {
              const a = answersByKey[q.question_key];
              const qAssets = (assets || []).filter((x) => x.question_key === q.question_key);
              const prompt = fr && qFr(q.question_key).prompt ? qFr(q.question_key).prompt : q.prompt;
              return (
                <div key={q.question_key}>
                  <div style={{ fontWeight: 600, fontSize: "1rem" }}>{prompt}</div>
                  <PreviewValue a={a} assets={qAssets} t={t} />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function PreviewValue({ a, assets, t }) {
  if (!a) return <p className="muted-sm">{t("notTouchedYet")}</p>;
  if (a.skipped) return <p className="muted-sm">{t("skippedDot")}</p>;
  const j = a.value_json ? JSON.parse(a.value_json) : {};
  return (
    <div className="muted-sm" style={{ color: "var(--qf-ink)" }}>
      {a.value_text && <p style={{ whiteSpace: "pre-wrap" }}>{a.value_text}</p>}
      {j.choice && <p>{j.choice}</p>}
      {j.value != null && <p>{j.value}/100</p>}
      {Array.isArray(j.selected) && <p>{[...j.selected, j.other].filter(Boolean).join(", ")}</p>}
      {Array.isArray(j.pairs) && j.pairs.map((p, i) => <p key={i}>{p.left} → {p.right}</p>)}
      {Array.isArray(j.topics) && j.topics.map((t, i) => <p key={i}>{t.topic}, knows {t.a}, comfort {t.b}</p>)}
      {Array.isArray(j.items) && j.items.map((it, i) => <p key={i}>{it.url} {it.why && `, ${it.why}`}{it.borrow && `, borrow: ${it.borrow}`}{it.avoid && `, avoid: ${it.avoid}`}</p>)}
      {j.type && <p>{j.type}{j.suffix ? `-${j.suffix}` : ""}</p>}
      {assets.map((x) => <p key={x.id}>{x.kind}: {x.title || x.external_url || x.mime}</p>)}
      {a.transcript_text && <p><em>Transcript:</em> {a.transcript_text}</p>}
    </div>
  );
}

function Completion({ t }) {
  return (
    <div className="app-shell">
      <div className="qf-wrap qf-section">
        <div className="done">
          <div className="cover"><img src="/img/done.jpg" alt="" /></div>
          <div className="done__body">
            <span className="done__check" aria-hidden>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            </span>
            <h1 className="dash__title">{t("thanksTitle")}</h1>
            <p className="qf-lead">{t("thanksBody")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Centered({ title, children }) {
  return (
    <div className="app-shell qf-section"><div className="qf-wrap center-narrow qf-stack">
      <h1 className="qf-title">{title}</h1>{children && <p className="qf-lead">{children}</p>}
    </div></div>
  );
}
